import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { useCRM } from './CRMContext';
import crmApi from '../utils/crmApi';

const SubscriptionContext = createContext();

export const useSubscription = () => {
    const context = useContext(SubscriptionContext);
    if (!context) {
        throw new Error('useSubscription must be used within SubscriptionProvider');
    }
    return context;
};

// Minimal local fallback for display only (backend is source of truth)
const SUBSCRIPTION_TIERS = {
    FREE: { name: 'Free' },
    TRIAL: { name: 'Trial' },
    STARTER: { name: 'Starter' },
    PROFESSIONAL: { name: 'Professional' },
    ENTERPRISE: { name: 'Enterprise' }
};

export const SubscriptionProvider = ({ children }) => {
    const { currentAgency } = useCRM();
    const [subscription, setSubscription] = useState(null);
    const [planCatalog, setPlanCatalog] = useState(null);
    const [loading, setLoading] = useState(true);
    const [usage, setUsage] = useState({
        clients: 0,
        accounts: 0,
        users: 0
    });

    // Check if user is authenticated
    const token = typeof window !== 'undefined' ? localStorage.getItem('crm_token') : null;
    const isAuthenticated = !!token;

    // Load subscription data
    useEffect(() => {
        // Only load plan catalog if authenticated
        if (isAuthenticated) {
            loadPlanCatalog();
        } else {
            setLoading(false);
        }
    }, [isAuthenticated]);

    useEffect(() => {
        // Only load subscription data if authenticated
        if (isAuthenticated) {
            loadSubscriptionData();
        } else {
            setLoading(false);
        }
    }, [currentAgency, isAuthenticated]);

    const loadSubscriptionData = async () => {
        // Don't make API call if not authenticated
        if (!isAuthenticated) {
            setLoading(false);
            return;
        }

        try {
            setLoading(true);
            
            if (!currentAgency?._id) {
                // Default to free tier if agency not selected yet
                setSubscription({
                    tier: 'FREE',
                    status: 'active',
                    features: {},
                    limits: { maxClients: 10, maxAccounts: 2, maxUsers: 1 }
                });
                setUsage({ clients: 0, accounts: 0, users: 0 });
                return;
            }
            
            // Fetch subscription details for current agency
            const res = await crmApi.get(`/agencies/${currentAgency._id}/subscription`);
            if (res.data?.success) {
                const sub = res.data.subscription;
                const planId = (sub.plan?.id || 'trial').toUpperCase();
                const features = sub.features || {};
                const seatsMax = sub.seats?.max ?? 1;
                const normalizedLimits = {
                    maxClients: features.maxClients ?? 10,
                    maxAccounts: features.maxAccounts ?? 2,
                    // Map users to seat max; -1 means unlimited
                    maxUsers: seatsMax === -1 ? Number.MAX_SAFE_INTEGER : seatsMax
                };

                setSubscription({
                    tier: planId,
                    status: sub.status || 'active',
                    billingCycle: sub.plan?.billingPeriod || 'month',
                    trialEnds: sub.trialEnds || null,
                    features,
                    limits: normalizedLimits
                });

                // Basic usage placeholder (could be fetched from analytics later)
                setUsage((prev) => ({ ...prev }));
            } else {
                throw new Error(res.data?.message || 'Failed to load subscription');
            }
        } catch (error) {
            // Silently handle 401 errors (user not authenticated)
            if (error.response?.status !== 401) {
                console.error('Failed to load subscription data:', error);
            }
            // Default to free tier
            setSubscription({
                tier: 'FREE',
                status: 'active',
                features: {},
                limits: { maxClients: 10, maxAccounts: 2, maxUsers: 1 }
            });
        } finally {
            setLoading(false);
        }
    };

    const loadPlanCatalog = async () => {
        // Don't make API call if not authenticated
        if (!isAuthenticated) {
            return;
        }

        try {
            // sessionStorage cache with 1h TTL
            const cacheKey = 'planCatalogV1';
            const cached = sessionStorage.getItem(cacheKey);
            if (cached) {
                const parsed = JSON.parse(cached);
                if (Date.now() - parsed.timestamp < 3600 * 1000) {
                    setPlanCatalog(parsed.data);
                    return;
                }
            }
            const res = await crmApi.get('/subscription/plans');
            if (res.data?.success) {
                setPlanCatalog(res.data.plans);
                sessionStorage.setItem(cacheKey, JSON.stringify({ timestamp: Date.now(), data: res.data.plans }));
            }
        } catch (e) {
            // Silently handle 401 errors (user not authenticated)
            if (e.response?.status !== 401) {
                // ignore other errors, fallback to local constants
            }
        }
    };

    // Check if a feature is enabled
    const hasFeature = (featureName) => {
        if (!subscription) return false;
        return subscription.features[featureName] === true;
    };

    // Check if user can perform an action (within limits)
    const canPerformAction = (actionType) => {
        if (!subscription) return false;
        
        const limits = subscription.limits;
        const currentUsage = usage;
        
        switch (actionType) {
            case 'createClient':
                return currentUsage.clients < limits.maxClients;
            case 'createAccount':
                return currentUsage.accounts < limits.maxAccounts;
            case 'addUser':
                return currentUsage.users < limits.maxUsers;
            default:
                return true;
        }
    };

    // Get upgrade suggestions
    const getUpgradeSuggestions = () => {
        if (!subscription) return [];
        const order = planCatalog ? Object.keys(planCatalog) : ['trial','starter','professional','enterprise'];
        const currentKey = (subscription.tier || 'TRIAL').toLowerCase();
        const idx = order.indexOf(currentKey);
        if (idx === -1 || idx === order.length - 1) return [];
        const nextKey = order[idx + 1];
        const next = planCatalog?.[nextKey];
        if (!next) return [];
        return [{
            tier: nextKey.toUpperCase(),
            reason: `Upgrade to ${next.name}`,
            features: Object.keys(next.features || {})
        }];
    };

    // Get usage warnings
    const getUsageWarnings = () => {
        if (!subscription) return [];
        
        const warnings = [];
        const limits = subscription.limits;
        const currentUsage = usage;
        
        // Check if approaching limits (80% threshold)
        if (currentUsage.clients >= limits.maxClients * 0.8) {
            warnings.push({
                type: 'warning',
                message: `You're using ${currentUsage.clients}/${limits.maxClients} clients (${Math.round((currentUsage.clients / limits.maxClients) * 100)}%)`,
                action: 'Consider upgrading to add more clients'
            });
        }
        
        if (currentUsage.accounts >= limits.maxAccounts * 0.8) {
            warnings.push({
                type: 'warning',
                message: `You're using ${currentUsage.accounts}/${limits.maxAccounts} accounts (${Math.round((currentUsage.accounts / limits.maxAccounts) * 100)}%)`,
                action: 'Consider upgrading to add more accounts'
            });
        }
        
        if (currentUsage.users >= limits.maxUsers * 0.8) {
            warnings.push({
                type: 'warning',
                message: `You're using ${currentUsage.users}/${limits.maxUsers} users (${Math.round((currentUsage.users / limits.maxUsers) * 100)}%)`,
                action: 'Consider upgrading to add more users'
            });
        }
        
        return warnings;
    };

    // Get available tiers for upgrade
    const getAvailableTiers = () => {
        if (!subscription) return [];
        const order = planCatalog ? Object.keys(planCatalog) : ['trial','starter','professional','enterprise'];
        const currentKey = (subscription.tier || 'TRIAL').toLowerCase();
        const currentIndex = order.indexOf(currentKey);
        const remaining = order.slice(currentIndex + 1);
        return remaining.map(key => ({
            tier: key.toUpperCase(),
            name: planCatalog?.[key]?.name || SUBSCRIPTION_TIERS[key.toUpperCase()]?.name || key
        }));
    };

    const value = useMemo(() => ({
        subscription,
        usage,
        loading,
        planCatalog,
        hasFeature,
        canPerformAction,
        getUpgradeSuggestions,
        getUsageWarnings,
        getAvailableTiers,
        SUBSCRIPTION_TIERS,
        loadSubscriptionData
    }), [subscription, usage, loading, planCatalog]);

    return (
        <SubscriptionContext.Provider value={value}>
            {children}
        </SubscriptionContext.Provider>
    );
};




